import IsLoading from "components/common/IsLoading";
import { ArrowDown, ArrowUp } from "lucide-react";
import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import riyal from "assets/images/icons/riyal.svg";
import { DatePicker, DateRangePicker } from "rsuite";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  format,
  subDays,
  subWeeks,
  subMonths,
  subYears,
} from "date-fns";
import { getMinMaxDate } from "components/common/Utils";
import Switch from "components/common/Switch";
import { getOverviewComparisonChart } from "store/actions/dashboardSlice";
import { ShimmerThumbnail } from "react-shimmer-effects";

const RevenueChart = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loading6, comparisonChart, overviewDashboard } = useSelector(
    (state) => state.Dashboards
  );
  const defaultDateRange = () => {
    const today = new Date();
    return [today, today];
  };

  const [rangeType, setRangeType] = useState("hour");
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [comparisonDateRange, setComparisonDateRange] = useState(null);
  const [singleDate, setSingleDate] = useState(null);
  const [isCompared, setIsCompared] = useState(false);
  const [hasActualBookingDateChosen, setHasActualBookingDateChosen] =
    useState(false);
  const [updatedIsCompared, setUpdatedIsCompared] = useState(false);

  const [displayedDateRange, setDisplayedDateRange] = useState(null);
  const [displayedComparisonDateRange, setDisplayedComparisonDateRange] =
    useState(null);
  const [displayedRangeType, setDisplayedRangeType] = useState("hour");

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initial load
    getAllData({ actionType: "INITIAL" });
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    // Skip if not initialized or invalid dateRange
    if (!isInitialized || !dateRange || dateRange.length === 0) return;

    // Skip if comparison is enabled but no comparison date is selected
    if (
      isCompared &&
      (!comparisonDateRange || comparisonDateRange.length === 0)
    )
      return;

    getAllData({ actionType: "SEARCH" });
  }, [
    isInitialized,
    rangeType,
    dateRange,
    comparisonDateRange,
    isCompared,
    hasActualBookingDateChosen,
  ]);

  const getAllData = (action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const isReset = actionType === "RESET";

    const payload = {
      spaceTypeInt: [],
      spaces: [],
      branches: [],
      hasActualBookingDateChosen: isReset ? false : hasActualBookingDateChosen,
      rangeType: isReset ? "hour" : rangeType, // hour, date, week, month
      isCompared: isReset ? false : isCompared,
      dateRange: isReset
        ? getMinMaxDate(defaultDateRange())
        : getMinMaxDate(dateRange),
      comparisonDateRange:
        isReset || !isCompared ? {} : getMinMaxDate(comparisonDateRange),
    };

    dispatch(getOverviewComparisonChart(payload));

    setUpdatedIsCompared(isReset ? false : isCompared);
    // Update displayed dates when search/reset is clicked
    setDisplayedDateRange(
      isReset ? getMinMaxDate(defaultDateRange()) : getMinMaxDate(dateRange)
    );
    setDisplayedComparisonDateRange(
      isReset ? null : getMinMaxDate(comparisonDateRange)
    );
    setDisplayedRangeType(isReset ? "hour" : rangeType);
  };

  const mainData = comparisonChart?.mainChartData || [];
  const comparisonData = comparisonChart?.comparisonChartData || [];

  const hasValidData = mainData.length > 0 || comparisonData.length > 0;

  const xAxisLabels =
    mainData?.length >= comparisonData?.length
      ? mainData?.map((item) => item.label) || []
      : comparisonData?.map((item) => item.label) || [];

  const series = updatedIsCompared
    ? [
        {
          name: "Current Revenue",
          data: mainData.map((item) => item.totalRevenue),
        },
        {
          name: "Previous Revenue",
          data: comparisonData.map((item) => item.totalRevenue),
        },
      ]
    : [
        {
          name: "Revenue",
          data: mainData.map((item) => item.totalRevenue),
        },
      ];

  const options = {
    chart: {
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },

    // title: {
    //   text: "Revenue Comparison",
    //   align: "left",
    // },
    xaxis: {
      categories: xAxisLabels,
      // title: {
      //   text: "Date",
      // },
    },
    yaxis: {
      labels: {
        formatter: function (value) {
          return `SAR ${value}`;
        },
      },
      // title: {
      //   text: "Total Revenue",
      // },
    },
    colors: updatedIsCompared ? ["#c4d26e", "#ABDBFF"] : ["#c4d26e"],
    legend: {
      position: "top",
      horizontalAlign: "center",
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
    noData: {
      text: "No data available",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#999",
        fontSize: "16px",
      },
    },
  };

  // Set default dates based on range type
  const setDefaultDates = () => {
    const now = new Date();
    let newDateRange = null;
    let newSingleDate = null;

    switch (rangeType) {
      case "hour": // Day
        newSingleDate = now;
        newDateRange = [now, now];
        break;
      case "date": // Week
        const weekStart = startOfWeek(now, { weekStartsOn: 0 });
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 0 });
        newDateRange = [weekStart, weekEnd];
        newSingleDate = weekStart;
        break;
      case "week": // Month
        const monthStart = startOfMonth(now);
        const monthEnd = endOfMonth(monthStart);
        newDateRange = [monthStart, monthEnd];
        newSingleDate = monthStart;
        break;
      case "month": // Year
        const yearStart = startOfYear(now);
        const yearEnd = endOfYear(yearStart);
        newDateRange = [yearStart, yearEnd];
        newSingleDate = yearStart;
        break;
      default:
        break;
    }

    setSingleDate(newSingleDate);
    setDateRange(newDateRange);

    // Set comparison dates if isCompared is true
    if (isCompared && newDateRange) {
      const [start, end] = newDateRange;
      let prevStart, prevEnd;

      switch (rangeType) {
        case "hour": // Day
          prevStart = subDays(start, 1);
          prevEnd = subDays(end, 1);
          break;
        case "date": // Week
          prevStart = subWeeks(start, 1);
          prevEnd = subWeeks(end, 1);
          break;
        case "week": // Month
          prevStart = subMonths(start, 1);
          prevEnd = subMonths(end, 1);
          break;
        case "month": // Year
          prevStart = subYears(start, 1);
          prevEnd = subYears(end, 1);
          break;
        default:
          prevStart = start;
          prevEnd = end;
      }

      setComparisonDateRange([prevStart, prevEnd]);
    } else {
      setComparisonDateRange(null);
    }
  };

  // Set default dates on component mount and when rangeType changes
  useEffect(() => {
    setDefaultDates();
  }, [rangeType]);

  const handleReset = () => {
    setIsCompared(false);
    setComparisonDateRange(null);
    setRangeType("hour"); // Reset to default range type
    setDefaultDates(); // This will set the default dates for "hour" type
    setHasActualBookingDateChosen(false);
    getAllData({ actionType: "RESET" });
  };

  const rangeOptions = [
    { label: "Day", value: "hour" },
    { label: "Week", value: "date" },
    { label: "Month", value: "week" },
    { label: "Year", value: "month" },
  ];

  // Calculate the previous period based on the current date and range type
  const getPreviousPeriod = (date) => {
    if (!date) return null;

    switch (rangeType) {
      case "hour": // Day
        return subDays(date, 1);
      case "date": // Week
        return subWeeks(date, 1);
      case "week": // Month
        return subMonths(date, 1);
      case "month": // Year
        return subYears(date, 1);
      default:
        return date;
    }
  };

  // Handle single date change for Day selection
  const handleSingleDateChange = (date) => {
    setSingleDate(date);
    if (date) {
      // For single day, set range as [date, date] for consistency
      const newDateRange = [date, date];
      setDateRange(newDateRange);

      // Auto-set comparison date if isCompared is true
      if (isCompared) {
        const prevDate = getPreviousPeriod(date);
        setComparisonDateRange([prevDate, prevDate]);
      }
    } else {
      setDateRange(null);
      setComparisonDateRange(null);
    }
  };

  // Handle date range change for Week/Month/Year selections
  const handleDateRangeChange = (range) => {
    if (!range || !range[0]) {
      setDateRange(null);
      setSingleDate(null);
      setComparisonDateRange(null);
      return;
    }

    const [selectedDate] = range;
    setSingleDate(selectedDate);

    let adjustedStart = selectedDate;
    let adjustedEnd = selectedDate;

    switch (rangeType) {
      case "date": // Week
        adjustedStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
        adjustedEnd = endOfWeek(adjustedStart, { weekStartsOn: 0 });
        break;
      case "week": // Month
        adjustedStart = startOfMonth(selectedDate);
        adjustedEnd = endOfMonth(selectedDate);
        break;
      case "month": // Year
        adjustedStart = startOfYear(selectedDate);
        adjustedEnd = endOfYear(selectedDate);
        break;
      default:
        break;
    }

    const newDateRange = [adjustedStart, adjustedEnd];
    setDateRange(newDateRange);

    // Auto-set comparison date range if isCompared is true
    if (isCompared) {
      let prevStart, prevEnd;

      switch (rangeType) {
        case "date": // Week
          prevStart = subWeeks(adjustedStart, 1);
          prevEnd = endOfWeek(prevStart, { weekStartsOn: 0 });
          break;
        case "week": // Month
          // For month comparison, we need to get the same day in previous month
          // then calculate start and end of that month
          const prevMonthSameDay = subMonths(selectedDate, 1);
          prevStart = startOfMonth(prevMonthSameDay);
          prevEnd = endOfMonth(prevMonthSameDay); // This will give correct end date (31 for May)
          break;
        case "month": // Year
          prevStart = subYears(adjustedStart, 1);
          prevEnd = endOfYear(prevStart);
          break;
        default:
          prevStart = subDays(adjustedStart, 1);
          prevEnd = subDays(adjustedEnd, 1);
      }

      setComparisonDateRange([prevStart, prevEnd]);
    }
  };

  // Handle comparison date range change
  const handleComparisonDateRangeChange = (range) => {
    if (!range || !range[0]) {
      setComparisonDateRange(null);
      return;
    }

    const [selectedDate] = range;
    let adjustedStart = selectedDate;
    let adjustedEnd = selectedDate;

    switch (rangeType) {
      case "hour": // Day
        // No adjustment needed for day comparison
        break;
      case "date": // Week
        adjustedStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
        adjustedEnd = endOfWeek(adjustedStart, { weekStartsOn: 0 });
        break;
      case "week": // Month
        adjustedStart = startOfMonth(selectedDate);
        adjustedEnd = endOfMonth(adjustedStart);
        break;
      case "month": // Year
        adjustedStart = startOfYear(selectedDate);
        adjustedEnd = endOfYear(adjustedStart);
        break;
      default:
        break;
    }

    setComparisonDateRange([adjustedStart, adjustedEnd]);
  };

  // Update comparison date when isCompared is toggled
  useEffect(() => {
    if (isCompared && dateRange) {
      const [start, end] = dateRange;

      let prevStart, prevEnd;

      switch (rangeType) {
        case "hour": // Day
          prevStart = subDays(start, 1);
          prevEnd = subDays(end, 1);
          break;
        case "date": // Week
          prevStart = subWeeks(start, 1);
          prevEnd = subWeeks(end, 1);
          break;
        case "week": // Month
          prevStart = subMonths(start, 1);
          prevEnd = subMonths(end, 1);
          break;
        case "month": // Year
          prevStart = subYears(start, 1);
          prevEnd = subYears(end, 1);
          break;
        default:
          prevStart = start;
          prevEnd = end;
      }

      setComparisonDateRange([prevStart, prevEnd]);
    } else {
      setComparisonDateRange(null);
    }
  }, [isCompared, dateRange, rangeType]);

  // Reset date selections when range type changes
  const handleRangeTypeChange = (newRangeType) => {
    setRangeType(newRangeType);
    setSingleDate(null);
    setDateRange(null);
    setComparisonDateRange(null);
  };

  const renderDatePicker = () => {
    if (rangeType === "hour") {
      // Day selection - use DatePicker
      return (
        <div className="rsuite-container">
          <DatePicker
            oneTap
            placeholder="Select date"
            format="yyyy-MM-dd"
            onChange={handleSingleDateChange}
            value={singleDate}
            placement="auto"
            appearance="default"
            style={{ width: 260 }}
            weekStart={0}
            cleanable={false}
          />
        </div>
      );
    } else {
      // Week/Month/Year selection - use DateRangePicker with oneTap
      return (
        <div className="rsuite-container">
          <DateRangePicker
            oneTap
            placeholder={`Select ${rangeOptions
              .find((opt) => opt.value === rangeType)
              ?.label.toLowerCase()}`}
            format="yyyy-MM-dd"
            onChange={handleDateRangeChange}
            value={dateRange}
            placement="auto"
            appearance="default"
            style={{ width: 320 }}
            hoverRange={
              rangeType === "date"
                ? "week"
                : rangeType === "week"
                ? "month"
                : "month"
            }
            weekStart={0}
            cleanable={false}
          />
        </div>
      );
    }
  };

  const renderComparisonDatePicker = () => {
    if (!isCompared) return null;

    if (rangeType === "hour") {
      // Day comparison - use DatePicker
      return (
        <div className="rsuite-container">
          <DatePicker
            oneTap
            placeholder="Select comparison date"
            format="yyyy-MM-dd"
            onChange={(date) => handleComparisonDateRangeChange([date, date])}
            value={comparisonDateRange ? comparisonDateRange[0] : null}
            placement="auto"
            appearance="default"
            style={{ width: 260 }}
            weekStart={0}
            cleanable={false}
          />
        </div>
      );
    } else {
      // Week/Month/Year comparison - use DateRangePicker with oneTap
      return (
        <div className="rsuite-container">
          <DateRangePicker
            oneTap
            placeholder={`Select comparison ${rangeOptions
              .find((opt) => opt.value === rangeType)
              ?.label.toLowerCase()}`}
            format="yyyy-MM-dd"
            onChange={handleComparisonDateRangeChange}
            value={comparisonDateRange}
            placement="auto"
            appearance="default"
            style={{ width: 320 }}
            hoverRange={
              rangeType === "date"
                ? "week"
                : rangeType === "week"
                ? "month"
                : "month"
            }
            weekStart={0}
            cleanable={false}
          />
        </div>
      );
    }
  };

  const formatDateRange = (newDate, rangeType) => {
    if (!newDate || !newDate.min) return "";

    const { min, max } = newDate;

    switch (rangeType) {
      case "hour": // Day
        return min; // Already in yyyy-MM-dd format
      case "date": // Week
      case "week": // Month
      case "month": // Year
        return `${min} ~ ${max}`;
      default:
        return "";
    }
  };

  return (
    <>
      {loading6 ? (
        <ShimmerThumbnail height={450} className="m-0" rounded />
      ) : (
        <div className="third_row">
          {/* FILTERS */}
          <div className="revenue_filters_row common_filter d-flex justify-content-between flex-wrap gap-5 align-items-end">
            <div className="d-flex gap-3 flex-wrap">
              {/* Range Type Buttons */}
              <div>
                <label htmlFor="dateRange" className="mb-1">
                  Select Type
                </label>
                <div className="tab_buttons">
                  {rangeOptions.map((option) => (
                    <button
                      key={option.value}
                      className={rangeType === option.value ? "active" : ""}
                      onClick={() => {
                        handleRangeTypeChange(option.value);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Picker */}
              <div className="picker" style={{ maxWidth: 225 }}>
                <label htmlFor="dateRange" className="mb-1">
                  Select Date
                </label>
                {renderDatePicker()}
              </div>

              {/* Comparison Date Picker */}
              {isCompared && (
                <div className="picker" style={{ maxWidth: 225 }}>
                  <label htmlFor="dateRange" className="mb-1">
                    Select Compare Date
                  </label>

                  {renderComparisonDatePicker()}
                </div>
              )}

              <div className="d-flex  gap-2 flex-direction-column">
                <label htmlFor="isCompared">{t("Compare")}</label>
                <Switch
                  checked={isCompared}
                  handleOnChange={() => {
                    setIsCompared(!isCompared);
                  }}
                />
              </div>

              <div className="d-flex  gap-2 flex-direction-column">
                <label htmlFor="hasActualBookingDateChosen">
                  {t("Filter by Actual Booking Date")}
                </label>
                <Switch
                  checked={hasActualBookingDateChosen}
                  handleOnChange={() => {
                    setHasActualBookingDateChosen(!hasActualBookingDateChosen);
                  }}
                />
              </div>
            </div>
            <div>
              <div className="buttons">
                <button
                  className="search_btn"
                  onClick={() => getAllData({ actionType: "SEARCH" })}
                >
                  {t("Apply")}
                </button>
                <button className="reset_btn" onClick={handleReset}>
                  {t("Reset")}
                </button>
              </div>
            </div>
          </div>

          <div className="g_between">
            <div>
              <div className="">
                <h2 className="fs_20">{t("Total Revenue")}</h2>

                {displayedDateRange && (
                  <span className="fs_14 text_dark">
                    Selected Date:{" "}
                    {formatDateRange(displayedDateRange, displayedRangeType)}
                  </span>
                )}
              </div>

              <h3 className="fs_40">
                <img src={riyal} width={25} height={25} />
                {comparisonChart?.mainTotals?.totalRevenue?.toFixed(2) ?? "-"}
              </h3>

              <div className="d-flex align-items-center">
                {updatedIsCompared && (
                  <div
                    className={`revenue_div ${
                      comparisonChart?.revenueTrend === "positive"
                        ? "up"
                        : "down"
                    }`}
                  >
                    <span
                      className={`fs_14 ${
                        comparisonChart?.revenueTrend === "positive"
                          ? "text_primary"
                          : "text_danger"
                      }`}
                    >
                      {comparisonChart?.revenueTrend === "positive" ? (
                        <ArrowUp className="text_primary" size={18} />
                      ) : (
                        <ArrowDown className="text_danger" size={18} />
                      )}
                      {comparisonChart?.revenueChange}
                    </span>
                  </div>
                )}

                <span className="fs_14 mx-2">
                  {t("Number of bookings")}{" "}
                  {comparisonChart?.mainTotals?.totalCount ?? 0}
                </span>
              </div>
            </div>
            {updatedIsCompared && (
              <div>
                <div className="">
                  <h2 className="fs_20">{t("Total Revenue")}</h2>
                  {displayedComparisonDateRange && (
                    <span className="fs_14 text_dark">
                      Selected Date:{" "}
                      {formatDateRange(
                        displayedComparisonDateRange,
                        displayedRangeType
                      )}
                    </span>
                  )}
                </div>

                <h3 className="fs_40">
                  <img src={riyal} width={25} height={25} />
                  {comparisonChart?.comparisonTotals?.totalRevenue?.toFixed(
                    2
                  ) ?? "-"}
                </h3>

                <div className="d-flex align-items-center">
                  <span className="fs_14 mx-2">
                    {t("Number of bookings")}{" "}
                    {comparisonChart?.comparisonTotals?.totalCount ?? 0}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div>
            {hasValidData ? (
              <ReactApexChart
                options={options}
                series={series}
                type="area"
                height={450}
              />
            ) : (
              <p>No chart data available</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RevenueChart;
